import { useSnackbar } from 'notistack';
import React from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useInviteDriver } from '../../../../app/ApiGen';
import { errorSnackbar } from '../../../../components/ErrorSnackbar';
import { Form } from '../../../../components/Form';
import { Card, type OnSubmit, Section, Workspace } from '../../../../design-system';
import { standardiseError } from '../../../../utils/error';
import {
  type UserCreateDetails,
  userCreateFields,
  userCreateValidation,
} from '../../../../utils/users/FieldDefinitions';

const CreateUserView: React.FC = () => {
  const { slug: orgSlug } = useParams<{ slug: string }>();
  const { push } = useHistory();
  const { mutate: inviteDriver } = useInviteDriver({ orgSlug });
  const { closeSnackbar, enqueueSnackbar } = useSnackbar();

  const initialValues: UserCreateDetails = { email: '', name: '' };

  const onSubmit = React.useCallback<OnSubmit<UserCreateDetails>>(
    async (values, { setSubmitting, setFieldError }) => {
      try {
        setFieldError('email', undefined);
        setSubmitting(true);
        const { data } = await inviteDriver({
          data: { type: 'users', attributes: { email: values.email, name: values.name } },
        });
        enqueueSnackbar('New user account created', { variant: 'success' });
        enqueueSnackbar(`Invitation email sent to ${data.attributes.email}`, { variant: 'success' });
        push(`/organisations/${orgSlug}/users`);
      } catch (e) {
        setSubmitting(false);
        const error = standardiseError(e);
        if (error.title === 'AlreadyExistsError') {
          setFieldError('email', 'User with this email address already exists');
        } else {
          errorSnackbar('Unable to create user account', closeSnackbar, enqueueSnackbar, e);
        }
      }
    },
    [closeSnackbar, enqueueSnackbar, inviteDriver, orgSlug, push],
  );

  return (
    <Workspace>
      <Section
        title="Invite new user to organisation"
        description="Send an invitation email for a customer to create a new Evnex account. The new user will be added
        to this organisation as a Driver."
      >
        <Card>
          <Form<UserCreateDetails>
            fields={userCreateFields}
            initialValues={initialValues}
            isEditing
            labels={{ save: 'Invite user' }}
            onSubmit={onSubmit}
            validationSchema={userCreateValidation}
          />
        </Card>
      </Section>
    </Workspace>
  );
};

export default CreateUserView;
